import { Node as ProseNode } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';
import { MathView } from './math-nodeview';
import { EditorView } from '@tiptap/pm/view';

export interface IMathPluginState {
  macros: { [cmd: string]: string };
  /**
   * Used to determine whether to place the cursor in the front- or back-most
   * position when expanding a math node, without overriding the default arrow
   * key behavior.
   */
  prevCursorPos: number;
}

// uniquely identifies the prosemirror-math plugin
const MATH_PLUGIN_KEY = new PluginKey<IMathPluginState>('prosemirror-math');

/**
 * Returns a function suitable for passing as a field in `EditorProps.nodeViews`.
 * @param displayMode TRUE for block math, FALSE for inline math.
 * @see https://prosemirror.net/docs/ref/#view.EditorProps.nodeViews
 */
export function createMathView(displayMode: boolean) {
  return (
    node: ProseNode,
    view: EditorView,
    getPos: boolean | (() => number | undefined),
  ): MathView => {
    const pluginState = MATH_PLUGIN_KEY.getState(view.state);
    if (!pluginState) {
      throw new Error('no math plugin!');
    }

    // set up NodeView
    const nodeView = new MathView(
      node,
      view,
      getPos as () => number,
      { katexOptions: { displayMode, macros: pluginState.macros } },
      MATH_PLUGIN_KEY,
    );

    return nodeView;
  };
}

export const mathPlugin = new Plugin<IMathPluginState>({
  key: MATH_PLUGIN_KEY,
  state: {
    init() {
      return {
        macros: {},
        prevCursorPos: 0,
      };
    },
    apply(_tr, value, oldState) {
      // produce updated state field for this plugin
      return {
        // these values are left unchanged
        macros: value.macros,
        // update with the second-most recent cursor pos
        prevCursorPos: oldState.selection.from,
      };
    },
  },
});
